import { IdeasTimespanEnum, VideoEmbedTypeEnum } from '../../../common/core-api';
import { type PageCmsPanel, type PageCmsPanels, panelTypesByTypename } from './panel-types';

const VIDEO_EMBED_TYPE_VALUES: Record<VideoEmbedTypeEnum, string> = {
  [VideoEmbedTypeEnum.LINK]: 'link',
  [VideoEmbedTypeEnum.CODE]: 'code',
  [VideoEmbedTypeEnum.VIDEO_TAG]: 'video_tag'
};

const IDEAS_TIMESPAN_VALUES: Record<IdeasTimespanEnum, string> = {
  [IdeasTimespanEnum.DAYS_7]: '7',
  [IdeasTimespanEnum.DAYS_14]: '14',
  [IdeasTimespanEnum.DAYS_30]: '30',
  [IdeasTimespanEnum.DAYS_60]: '60',
  [IdeasTimespanEnum.DAYS_90]: '90',
  [IdeasTimespanEnum.FOREVER]: 'forever'
};

const withUrlScheme = (url: string): string => (url && !/^https?:\/\//i.test(url) ? `https://${url}` : url);

const buildButtonConfig = (
  buttonConfig:
    | Extract<PageCmsPanel['settings'], { __typename: 'TextPanelSettings' | 'ImagePanelSettings' }>['buttonConfig']
    | null
) => ({
  display: buttonConfig?.display ?? 'none',
  category: buttonConfig?.category ?? '',
  text: buttonConfig?.text ?? '',
  url: buttonConfig?.url ?? '',
  externalUrl: withUrlScheme(buttonConfig?.externalUrl ?? '')
});

const buildPreviewPanelSettings = (settings: PageCmsPanel['settings']) => {
  const commonSettings = {
    type: panelTypesByTypename[settings.__typename].id,
    size: settings.size
  };

  switch (settings.__typename) {
    case 'TextPanelSettings':
      return {
        ...commonSettings,
        text: settings.text ?? '',
        buttonConfig: buildButtonConfig(settings.buttonConfig)
      };
    case 'ImagePanelSettings':
      return {
        ...commonSettings,
        imageId: Number(settings.imageFile?.id) || 0,
        imageWidth: settings.imageWidth ?? '',
        imageAltText: settings.imageAltText ?? '',
        imageRoundedCorners: settings.imageRoundedCorners ?? false,
        buttonConfig: buildButtonConfig(settings.buttonConfig)
      };
    case 'VideoPanelSettings':
      return {
        ...commonSettings,
        embed: VIDEO_EMBED_TYPE_VALUES[settings.embed ?? VideoEmbedTypeEnum.LINK],
        videoUrl: settings.videoUrl ?? '',
        videoEmbedCode: settings.videoEmbedCode ?? ''
      };
    case 'IdeasPanelSettings':
      return {
        ...commonSettings,
        ideasDisplay: settings.ideasDisplay ?? [],
        ideasDefault: settings.ideasDefault ?? settings.ideasDisplay?.[0],
        ideasTimespan: settings.timespan ? IDEAS_TIMESPAN_VALUES[settings.timespan] : undefined
      };
    case 'LeaderboardPanelSettings':
      return {
        ...commonSettings,
        leaderboardDisplay: settings.leaderboardDisplay ?? [],
        leaderboardDefault: settings.leaderboardDefault ?? settings.leaderboardDisplay?.[0]
      };
    case 'ChallengeCarouselPanelSettings':
      return {
        ...commonSettings,
        carouselFavourites: settings.favourites ?? false,
        hideDescription: settings.hideDescription ?? false,
        includeUserChallenges: settings.includeUserChallenges ?? false,
        carouselType: commonSettings.type
      };
    case 'BlogCarouselPanelSettings':
      return { ...commonSettings, carouselType: commonSettings.type };
    default:
      return commonSettings;
  }
};

export const buildPagePreviewSettings = (pageNavTitle: string, panels: PageCmsPanels) => ({
  panelData: panels.map((row) =>
    row.map((panel) => ({
      id: String(panel.id),
      settings: buildPreviewPanelSettings(panel.settings)
    }))
  ),
  page: { titleNav: pageNavTitle }
});
